/**
 * Collapses long rich text with a Read more / Read less control.
 * Collapsed state uses CSS line-clamp so clipping follows real line boxes (mixed weights, links, margins).
 * Line count: data-read-more-collapsed-lines and --product-description-collapsed-lines on the root.
 *
 * Init runs when the block enters the viewport (IntersectionObserver). Until then, CSS keeps the same
 * line clamp so long copy does not flash uncropped (see section-product-description.css).
 */

const supportsLineClamp = () =>
  typeof CSS !== 'undefined' &&
  (CSS.supports('-webkit-line-clamp', '3') || CSS.supports('line-clamp', '3'));

const getCollapsedLineCount = (root) => {
  const linesAttr = root.dataset.readMoreCollapsedLines;

  if (linesAttr) {
    const lines = parseInt(linesAttr, 10);

    if (!Number.isNaN(lines) && lines > 0) {
      return lines;
    }
  }

  const fromVar = getComputedStyle(root).getPropertyValue('--product-description-collapsed-lines').trim();

  if (fromVar) {
    const lines = parseInt(fromVar, 10);

    if (!Number.isNaN(lines) && lines > 0) {
      return lines;
    }
  }

  return 12;
};

const contentExceedsLineClamp = (rte, lineCount) => {
  const w = Math.max(1, Math.floor(rte.getBoundingClientRect().width));

  if (w < 2) {
    return null;
  }

  const mirror = document.createElement('div');
  mirror.className = rte.className;
  mirror.innerHTML = rte.innerHTML;
  mirror.setAttribute('aria-hidden', 'true');
  mirror.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    `width:${w}px`,
    'visibility:hidden',
    'pointer-events:none',
    'overflow:hidden',
    'display:-webkit-box',
    '-webkit-box-orient:vertical',
    `-webkit-line-clamp:${lineCount}`,
    `line-clamp:${lineCount}`,
  ].join(';');

  document.body.appendChild(mirror);
  const exceeds = mirror.scrollHeight > mirror.clientHeight + 3;
  document.body.removeChild(mirror);

  return exceeds;
};

const initProductDescriptionReadMore = (root) => {
  if (root.dataset.productDescriptionReadMoreInit === 'true') {
    return;
  }

  const rte = root.querySelector('.product-description__rte');
  const button = root.querySelector('.product-description__read-more');

  if (!rte || !button) {
    root.dataset.productDescriptionReadMoreMeasured = 'true';

    return;
  }

  const labelMore = button.dataset.labelMore;
  const labelLess = button.dataset.labelLess;

  if (!labelMore || !labelLess) {
    root.dataset.productDescriptionReadMoreMeasured = 'true';

    return;
  }

  if (!supportsLineClamp()) {
    root.dataset.productDescriptionReadMoreInit = 'true';
    root.dataset.productDescriptionReadMoreMeasured = 'true';

    return;
  }

  const lineCount = getCollapsedLineCount(root);

  const exceeds = contentExceedsLineClamp(rte, lineCount);

  if (exceeds === null) {
    const retries = parseInt(root.dataset.readMoreMeasureRetries || '0', 10);

    if (retries < 8) {
      root.dataset.readMoreMeasureRetries = String(retries + 1);
      requestAnimationFrame(() => initProductDescriptionReadMore(root));
    } else {
      delete root.dataset.readMoreMeasureRetries;
      root.dataset.productDescriptionReadMoreInit = 'true';
      root.dataset.productDescriptionReadMoreMeasured = 'true';
    }

    return;
  }

  delete root.dataset.readMoreMeasureRetries;

  root.dataset.productDescriptionReadMoreInit = 'true';
  root.dataset.productDescriptionReadMoreMeasured = 'true';

  if (!exceeds) {
    return;
  }

  root.classList.add('product-description__copy--collapsed');
  button.hidden = false;
  button.textContent = labelMore;

  if (typeof root.__productDescriptionReadMoreClick === 'function') {
    button.removeEventListener('click', root.__productDescriptionReadMoreClick);
  }

  root.__productDescriptionReadMoreClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const isExpanded = root.classList.toggle('product-description__copy--expanded');

    root.classList.toggle('product-description__copy--collapsed', !isExpanded);
    button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    button.textContent = isExpanded ? labelLess : labelMore;
  };

  button.addEventListener('click', root.__productDescriptionReadMoreClick);
};

/** @type {IntersectionObserver | null} */
let productDescriptionReadMoreObserver = null;

const ensureProductDescriptionReadMoreObserver = () => {
  if (productDescriptionReadMoreObserver) {
    return productDescriptionReadMoreObserver;
  }

  if (!('IntersectionObserver' in window)) {
    return null;
  }

  productDescriptionReadMoreObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          continue;
        }

        productDescriptionReadMoreObserver.unobserve(entry.target);
        initProductDescriptionReadMore(entry.target);
      }
    },
    {
      root: null,
      rootMargin: '0px 0px 160px 0px',
      threshold: 0,
    },
  );

  return productDescriptionReadMoreObserver;
};

const observeProductDescriptionReadMore = (root) => {
  if (root.dataset.productDescriptionReadMoreInit === 'true') {
    return;
  }

  if (root.dataset.productDescriptionReadMoreMeasured === 'true') {
    return;
  }

  const observer = ensureProductDescriptionReadMoreObserver();

  if (!observer) {
    initProductDescriptionReadMore(root);

    return;
  }

  observer.observe(root);
};

const observeAllProductDescriptionReadMore = () => {
  for (const root of document.querySelectorAll('[data-product-description-read-more]')) {
    observeProductDescriptionReadMore(root);
  }
};

const mountProductDescriptionYoutube = (container) => {
  if (container.dataset.productDescriptionYoutubeMounted === 'true') {
    return;
  }

  const template = container.querySelector('[data-product-description-youtube-template]');

  if (!template || !('content' in template)) {
    return;
  }

  container.dataset.productDescriptionYoutubeMounted = 'true';
  container.appendChild(template.content.cloneNode(true));

  const iframe = container.querySelector('.product-description__external-video-iframe');

  if (!iframe) {
    container.classList.add('product-description__external-video--ready');

    return;
  }

  const markReady = () => {
    container.classList.add('product-description__external-video--ready');
  };

  iframe.addEventListener('load', markReady, { once: true });
  iframe.addEventListener('error', markReady, { once: true });
};

const initProductDescriptionYoutube = (container) => {
  if (container.dataset.productDescriptionYoutubeInit === 'true') {
    return;
  }

  container.dataset.productDescriptionYoutubeInit = 'true';

  const playButton = container.querySelector('.product-description__youtube-play');

  if (playButton) {
    playButton.addEventListener('click', () => {
      mountProductDescriptionYoutube(container);
    });
  }
};

const observeAllProductDescriptionYoutube = () => {
  for (const container of document.querySelectorAll('[data-product-description-youtube]')) {
    initProductDescriptionYoutube(container);
  }
};

const remeasureProductDescriptionReadMore = (root) => {
  const rte = root.querySelector('.product-description__rte');
  const button = root.querySelector('.product-description__read-more');

  if (!rte || !button || !supportsLineClamp()) {
    return;
  }

  const labelMore = button.dataset.labelMore;
  const labelLess = button.dataset.labelLess;

  if (!labelMore || !labelLess) {
    return;
  }

  const lineCount = getCollapsedLineCount(root);
  const exceeds = contentExceedsLineClamp(rte, lineCount);

  if (exceeds === null) {
    return;
  }

  if (!exceeds) {
    root.classList.remove('product-description__copy--collapsed', 'product-description__copy--expanded');
    button.hidden = true;
    button.setAttribute('aria-expanded', 'false');

    return;
  }

  if (!root.classList.contains('product-description__copy--expanded')) {
    root.classList.add('product-description__copy--collapsed');
  }

  button.hidden = false;
  button.textContent = root.classList.contains('product-description__copy--expanded') ? labelLess : labelMore;
};

const bootProductDescription = () => {
  observeAllProductDescriptionReadMore();
  observeAllProductDescriptionYoutube();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootProductDescription);
} else {
  bootProductDescription();
}

document.addEventListener('shopify:section:load', bootProductDescription);

const resetProductDescriptionYoutube = () => {
  for (const container of document.querySelectorAll('[data-product-description-youtube]')) {
    delete container.dataset.productDescriptionYoutubeInit;
    delete container.dataset.productDescriptionYoutubeMounted;
    container.classList.remove('product-description__external-video--ready');

    for (const iframe of container.querySelectorAll('.product-description__external-video-iframe')) {
      iframe.remove();
    }
  }
};

document.addEventListener('product-description:updated', () => {
  const resetAndObserve = () => {
    resetProductDescriptionYoutube();

    for (const root of document.querySelectorAll('[data-product-description-read-more]')) {
      delete root.dataset.productDescriptionReadMoreInit;
      delete root.dataset.productDescriptionReadMoreMeasured;
      delete root.dataset.readMoreMeasureRetries;

      const btn = root.querySelector('.product-description__read-more');

      if (btn && typeof root.__productDescriptionReadMoreClick === 'function') {
        btn.removeEventListener('click', root.__productDescriptionReadMoreClick);
        delete root.__productDescriptionReadMoreClick;
      }

      if (btn) {
        btn.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
      }

      root.classList.remove('product-description__copy--collapsed', 'product-description__copy--expanded');
    }

    observeAllProductDescriptionReadMore();
    observeAllProductDescriptionYoutube();
  };

  resetAndObserve();

  requestAnimationFrame(() => {
    resetAndObserve();
  });

  window.setTimeout(resetAndObserve, 150);
});

document.addEventListener('quick-view:loaded', () => {
  const resetAndObserve = () => {
    resetProductDescriptionYoutube();

    for (const root of document.querySelectorAll('[data-product-description-read-more]')) {
      delete root.dataset.productDescriptionReadMoreInit;
      delete root.dataset.productDescriptionReadMoreMeasured;
      delete root.dataset.readMoreMeasureRetries;
    }

    observeAllProductDescriptionReadMore();
    observeAllProductDescriptionYoutube();
  };

  resetAndObserve();

  requestAnimationFrame(() => {
    resetAndObserve();
  });

  window.setTimeout(resetAndObserve, 150);
});

if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
  document.fonts.ready.then(() => {
    for (const root of document.querySelectorAll('[data-product-description-read-more]')) {
      if (root.dataset.productDescriptionReadMoreMeasured !== 'true') {
        continue;
      }

      remeasureProductDescriptionReadMore(root);
    }
  });
}
